import { useState, useCallback, useEffect } from "react";
import { Heart, X, Send, CheckCircle2 } from "lucide-react";
import { notify } from "@/components/common/Notification";
import {
  donationsAPI,
  type DonationCreateOrderPayload,
} from "@/projects/Jainboarding_User/services/api";
import {
  openRazorpayCheckout,
  getRazorpayKey,
} from "@/projects/Jainboarding_User/components/RazorpayPayment";

export interface DonorFormData {
  name: string;
  email: string;
  phone: string;
  organization: string;
  donationType: string;
  donationAmount: string;
  panNumber: string;
  aadharNumber: string;
  message: string;
}

const initialDonorFormData: DonorFormData = {
  name: "",
  email: "",
  phone: "",
  organization: "",
  donationType: "",
  donationAmount: "",
  panNumber: "",
  aadharNumber: "",
  message: "",
};

export interface DonorInquiryModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Called when the modal should close (e.g. overlay click or close button) */
  onClose: () => void;
  /** Optional callback after successful submit; receives the form data */
  onSuccess?: (data: DonorFormData) => void;
}

export default function DonorInquiryModal({
  open,
  onClose,
  onSuccess,
}: DonorInquiryModalProps) {
  const [formData, setFormData] = useState<DonorFormData>(initialDonorFormData);
  const [submitting, setSubmitting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<
    "verifying" | "success" | "idle"
  >("idle");
  const [errors, setErrors] = useState<
    Partial<Record<keyof DonorFormData, string>>
  >({});

  // Reset form when modal closes and handle body scroll
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
      setFormData(initialDonorFormData);
      setErrors({});
      setSubmitting(false);
      setVerifying(false);
      setVerificationStatus("idle");
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [open]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    let { value } = e.target;
    const { name } = e.target;

    if (name === "phone") {
      value = value.replace(/\D/g, "").slice(0, 10);
    }
    if (name === "donationAmount") {
      value = value.replace(/\D/g, "");
    }
    if (name === "aadharNumber") {
      value = value.replace(/\D/g, "").slice(0, 12);
    }

    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error when user types
    if (errors[name as keyof DonorFormData]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[name as keyof DonorFormData];
        return next;
      });
    }
  };

  const finishSuccess = useCallback(
    (data: DonorFormData) => {
      notify.success(
        "Donation Successful",
        "Thank you for your donation! Payment has been verified."
      );
      onSuccess?.(data);
      setFormData(initialDonorFormData);
      onClose();
    },
    [onSuccess, onClose]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Partial<Record<keyof DonorFormData, string>> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (formData.phone.length !== 10) {
      newErrors.phone = "Phone number must be 10 digits";
    }
    if (!formData.donationType) {
      newErrors.donationType = "Please select a donation type";
    }
    if (!formData.donationAmount.trim()) {
      newErrors.donationAmount = "Donation amount is required";
    }
    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    }

    const amountNum = Number(formData.donationAmount?.replace(/,/g, ""));

    if (amountNum > 2000 && !formData.panNumber.trim()) {
      newErrors.panNumber = "PAN Number is required for donation above ₹2000";
    }
    if (amountNum > 0 && amountNum <= 2000 && !formData.aadharNumber.trim()) {
      newErrors.aadharNumber =
        "Aadhar Number is required for donation ₹2000 or below";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // const firstError = Object.values(newErrors)[0];
      // notify.error("Validation Error", firstError);
      return;
    }

    const isValidAmount = Number.isFinite(amountNum) && amountNum > 0;

    if (!isValidAmount) {
      notify.error(
        "Invalid Donation Amount",
        "Please enter a donation amount greater than 0."
      );
      return;
    }

    if (!getRazorpayKey()) {
      notify.error(
        "Configuration Error",
        "Razorpay is not configured. Please set VITE_RAZORPAY_KEY_ID in your .env"
      );
      return;
    }

    try {
      setSubmitting(true);

      const amountStr = amountNum.toFixed(2);
      const createPayload: DonationCreateOrderPayload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        donation_type: formData.donationType || "donation",
        amount: amountStr,
        organization: formData.organization.trim() || undefined,
        pan_number: amountNum > 2000 ? formData.panNumber.trim() : "",
        aadhaar_number: amountNum <= 2000 ? formData.aadharNumber.trim() : "",
        message: formData.message.trim() || undefined,
      };

      const orderResponse = await donationsAPI.createOrder(createPayload);
      const orderId = orderResponse.order_id;
      const amountPaise =
        typeof orderResponse.amount === "number"
          ? orderResponse.amount
          : Math.round(amountNum * 100);

      openRazorpayCheckout({
        order_id: orderId,
        amount: amountPaise,
        currency: orderResponse.currency ?? "INR",
        name: "JBF Donation",
        description: `Donation - ${formData.donationType || "donation"}`,
        onSuccess: async response => {
          setVerifying(true);
          setVerificationStatus("verifying");
          const res = response as unknown as Record<string, unknown>;
          console.log(
            "Razorpay success response (full):",
            JSON.stringify(res, null, 2)
          );
          await new Promise(r => setTimeout(r, 0));
          try {
            // order_id from create-order API response (data.order_id)
            const razorpay_order_id = orderId;
            // payment_id and signature from Razorpay success response
            const razorpay_payment_id =
              response.razorpay_payment_id ?? res.razorpay_payment_id;
            const razorpay_signature =
              response.razorpay_signature ??
              res.razorpay_signature ??
              res.signature;
            if (!razorpay_order_id) {
              notify.error(
                "Verification Failed",
                "Order ID missing. Check create-order response."
              );
              return;
            }
            if (!razorpay_payment_id || !razorpay_signature) {
              notify.error(
                "Verification Failed",
                "Invalid payment response from Razorpay (missing payment_id or signature)."
              );
              return;
            }
            await donationsAPI.verifyPayment({
              razorpay_order_id,
              razorpay_payment_id: String(razorpay_payment_id),
              razorpay_signature: String(razorpay_signature),
            });
            setVerificationStatus("success");
            // Show success animation for 1.5 seconds before closing
            await new Promise(r => setTimeout(r, 1500));
            finishSuccess(formData);
          } catch (verifyErr) {
            console.error("Verify payment error:", verifyErr);
            notify.error(
              "Verification Failed",
              verifyErr instanceof Error
                ? verifyErr.message
                : "Payment verification failed. Please contact support."
            );
          } finally {
            setVerifying(false);
            setVerificationStatus("idle");
          }
        },
      });
    } catch (error: unknown) {
      console.error("Donor form submission error:", error);
      notify.error(
        "Submission Failed",
        error instanceof Error
          ? error.message
          : "Unable to create donation order. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return null;
  }

  const amountStr = formData.donationAmount?.replace(/,/g, "").trim();
  const amountNum = Number(amountStr);
  const hasValidAmount =
    amountStr !== "" && Number.isFinite(amountNum) && amountNum >= 0;
  const needsPan = hasValidAmount && amountNum > 2000;

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 p-2 sm:p-4">
      {/* Full Page Loader for verification */}
      {verifying && (
        <div className="fixed inset-0 z-[1000] flex flex-col items-center justify-center bg-black/70 backdrop-blur-[3px] transition-all">
          {verificationStatus === "verifying" ? (
            <>
              <div className="mb-4 h-16 w-16 animate-spin rounded-full border-4 border-[#D4A017] border-t-transparent" />
              <p className="text-lg font-bold text-[#D4A017]">
                Verifying your payment...
              </p>
            </>
          ) : (
            <>
              <div className="animate-in zoom-in mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-500/10 duration-500">
                <CheckCircle2 className="h-16 w-16 text-emerald-500 shadow-emerald-500/50" />
              </div>
              <p className="animate-in fade-in slide-in-from-bottom-2 text-2xl font-black tracking-tight text-emerald-500 duration-500">
                Payment Successful!
              </p>
            </>
          )}
          <p className="mt-2 text-sm text-gray-400">
            {verificationStatus === "verifying"
              ? "Please do not close this window or refresh the page."
              : "Thank you for your generous contribution."}
          </p>
        </div>
      )}
      <div className="relative max-h-[95vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-[#FFF9F0] shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white px-4 py-3 shadow-sm sm:px-6 sm:py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#7A1E1E] sm:h-10 sm:w-10">
                <Heart className="h-5 w-5 text-white sm:h-6 sm:w-6" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-[#7A1E1E] sm:text-2xl">
                  Donor & CSR Inquiry
                </h2>
                <p className="text-xs text-gray-600 sm:text-sm">
                  Submit your inquiry to contribute to the Jain Boarding
                  Federation
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 sm:p-6">
          <div className="space-y-4 sm:space-y-6">
            <div>
              <label
                htmlFor="donorName"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Your Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="donorName"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`w-full rounded-lg border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 ${
                  errors.name
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                    : "border-gray-300 focus:border-[#7A1E1E] focus:ring-[#7A1E1E]/20"
                }`}
                placeholder="Enter your name"
              />
              {errors.name && (
                <p className="mt-1 text-xs text-red-500">{errors.name}</p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="donorEmail"
                  className="mb-2 block text-sm font-medium text-gray-700"
                >
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="donorEmail"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full rounded-lg border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 ${
                    errors.email
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                      : "border-gray-300 focus:border-[#7A1E1E] focus:ring-[#7A1E1E]/20"
                  }`}
                  placeholder="Enter email address"
                />
                {errors.email && (
                  <p className="mt-1 text-xs text-red-500">{errors.email}</p>
                )}
              </div>
              <div>
                <label
                  htmlFor="donorPhone"
                  className="mb-2 block text-sm font-medium text-gray-700"
                >
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="donorPhone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full rounded-lg border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 ${
                    errors.phone
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                      : "border-gray-300 focus:border-[#7A1E1E] focus:ring-[#7A1E1E]/20"
                  }`}
                  placeholder="Enter 10-digit number"
                  maxLength={10}
                />
                {errors.phone && (
                  <p className="mt-1 text-xs text-red-500">{errors.phone}</p>
                )}
              </div>
            </div>

            <div>
              <label
                htmlFor="organization"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Organization/Company
              </label>
              <input
                type="text"
                id="organization"
                name="organization"
                value={formData.organization}
                onChange={handleChange}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-[#7A1E1E] focus:outline-none focus:ring-2 focus:ring-[#7A1E1E]/20"
                placeholder="Enter organization name (optional)"
              />
            </div>

            <div>
              <label
                htmlFor="donationType"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Donation Type <span className="text-red-500">*</span>
              </label>
              <select
                id="donationType"
                name="donationType"
                value={formData.donationType}
                onChange={handleChange}
                className={`w-full rounded-lg border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 ${
                  errors.donationType
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                    : "border-gray-300 focus:border-[#7A1E1E] focus:ring-[#7A1E1E]/20"
                }`}
              >
                <option value="">Select donation type</option>
                <option value="education">Education</option>
                <option value="standard">Standard</option>
                <option value="infrastructure">Infrastructure</option>
              </select>
              {errors.donationType && (
                <p className="mt-1 text-xs text-red-500">
                  {errors.donationType}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="donationAmount"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Donation Amount (₹) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                id="donationAmount"
                name="donationAmount"
                value={formData.donationAmount}
                onChange={handleChange}
                className={`w-full rounded-lg border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 ${
                  errors.donationAmount
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                    : "border-gray-300 focus:border-[#7A1E1E] focus:ring-[#7A1E1E]/20"
                }`}
                placeholder="Enter amount in ₹"
              />
              {errors.donationAmount && (
                <p className="mt-1 text-xs text-red-500">
                  {errors.donationAmount}
                </p>
              )}
            </div>

            {!hasValidAmount ? (
              <p className="text-sm text-gray-500">
                Enter donation amount above — PAN required if above ₹2000,
                Aadhar required if ₹2000 or below.
              </p>
            ) : needsPan ? (
              <div>
                <label
                  htmlFor="panNumber"
                  className="mb-2 block text-sm font-medium text-gray-700"
                >
                  PAN Number <span className="text-red-500">*</span>{" "}
                  <span className="font-normal text-gray-500">
                    (Above ₹2000)
                  </span>
                </label>
                <input
                  type="text"
                  id="panNumber"
                  name="panNumber"
                  value={formData.panNumber}
                  onChange={handleChange}
                  className={`w-full rounded-lg border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 ${
                    errors.panNumber
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                      : "border-gray-300 focus:border-[#7A1E1E] focus:ring-[#7A1E1E]/20"
                  }`}
                  placeholder="Enter PAN (required for donation above ₹2000)"
                  maxLength={10}
                />
                {errors.panNumber && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.panNumber}
                  </p>
                )}
              </div>
            ) : (
              <div>
                <label
                  htmlFor="aadharNumber"
                  className="mb-2 block text-sm font-medium text-gray-700"
                >
                  Aadhar Card Number <span className="text-red-500">*</span>{" "}
                  <span className="font-normal text-gray-500">
                    (₹2000 or below)
                  </span>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  id="aadharNumber"
                  name="aadharNumber"
                  value={formData.aadharNumber}
                  onChange={handleChange}
                  className={`w-full rounded-lg border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 ${
                    errors.aadharNumber
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                      : "border-gray-300 focus:border-[#7A1E1E] focus:ring-[#7A1E1E]/20"
                  }`}
                  placeholder="Enter 12-digit Aadhar (required for donation ₹2000 or below)"
                  maxLength={12}
                />
                {errors.aadharNumber && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.aadharNumber}
                  </p>
                )}
              </div>
            )}

            <div>
              <label
                htmlFor="donorMessage"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Message/Inquiry Details <span className="text-red-500">*</span>
              </label>
              <textarea
                id="donorMessage"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={5}
                className={`w-full rounded-lg border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 ${
                  errors.message
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                    : "border-gray-300 focus:border-[#7A1E1E] focus:ring-[#7A1E1E]/20"
                }`}
                placeholder="Please provide details about your inquiry or how you'd like to contribute..."
              />
              {errors.message && (
                <p className="mt-1 text-xs text-red-500">{errors.message}</p>
              )}
            </div>

            <div className="mt-6 flex justify-center sm:mt-8">
              <button
                type="submit"
                disabled={submitting}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#D4A017] px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#B8900F] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:px-8 sm:py-3 sm:text-base"
              >
                {submitting ? (
                  <>
                    <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Sending...
                  </>
                ) : (
                  <>
                    Make a Donation
                    <Send className="h-4 w-4 sm:h-5 sm:w-5" />
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
